import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { AccountUsageResponse } from "@/lib/api/routes/account-usage";
import { cn } from "@/lib/utils";
import { ArrowRight, RefreshCw } from "lucide-react";
import * as React from "react";

const POLL_INTERVAL_MS = 30_000;

type UsageEntry = { used: number; limit: number | "unlimited" };

function usagePercent(entry: UsageEntry): number {
  if (entry.limit === "unlimited") return 0;
  if (entry.limit === 0) return 100;
  return Math.min(100, Math.round((entry.used / entry.limit) * 100));
}

function usageColorClass(pct: number): string {
  if (pct > 95) return "text-red-600 dark:text-red-400";
  if (pct >= 80) return "text-yellow-600 dark:text-yellow-400";
  return "text-emerald-600 dark:text-emerald-400";
}

function barColorClass(pct: number): string {
  if (pct > 95) return "bg-red-500";
  if (pct >= 80) return "bg-yellow-500";
  return "bg-emerald-500";
}

function barTrackClass(pct: number): string {
  if (pct > 95) return "bg-red-100 dark:bg-red-950/40";
  if (pct >= 80) return "bg-yellow-100 dark:bg-yellow-950/40";
  return "bg-emerald-100 dark:bg-emerald-950/40";
}

function daysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  const diff = new Date(trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function tierLabel(tier: string): string {
  switch (tier) {
    case "trial":
      return "Trial";
    case "starter":
      return "Starter";
    case "professional":
      return "Professional";
    case "enterprise":
      return "Enterprise";
    case "legacy_professional":
      return "Professional (Legacy)";
    default:
      return tier;
  }
}

const METRIC_LABELS: Record<string, string> = {
  quotes: "Quotes",
  shipments: "Shipments",
  api_calls_today: "API Calls (today)",
  carriers: "Carriers",
  templates: "Templates",
};

function UsageBar({
  label,
  entry,
}: {
  label: string;
  entry: UsageEntry;
}) {
  const isUnlimited = entry.limit === "unlimited";
  const pct = usagePercent(entry);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span className={cn("tabular-nums font-medium", isUnlimited ? "text-muted-foreground" : usageColorClass(pct))}>
          {isUnlimited ? (
            <>{entry.used} / Unlimited</>
          ) : (
            <>
              {entry.used} / {entry.limit}
            </>
          )}
        </span>
      </div>
      {isUnlimited ? (
        <div className="h-2 w-full rounded-full bg-muted" />
      ) : (
        <div className={cn("h-2 w-full rounded-full", barTrackClass(pct))}>
          <div
            className={cn("h-full rounded-full transition-all duration-500", barColorClass(pct))}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function UsageWidget() {
  const [data, setData] = React.useState<AccountUsageResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);

  const fetchUsage = React.useCallback(async (isManual?: boolean) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await fetch("/api/account/usage", { credentials: "include" });
      if (!res.ok) {
        setError(true);
        return;
      }
      const json = await res.json();
      if (json.status === "success") {
        setData(json.data);
        setError(false);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    fetchUsage();
    const timer = setInterval(() => fetchUsage(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchUsage]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Usage & Limits</CardTitle>
          <CardDescription>Current month usage for your plan.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex justify-between">
                  <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                </div>
                <div className="h-2 w-full animate-pulse rounded-full bg-muted" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Usage & Limits</CardTitle>
          <CardDescription>Unable to load usage data.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" onClick={() => void fetchUsage(true)}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isTrial = data.subscription_tier === "trial";
  const isLegacy = data.is_legacy;
  const isPaid = ["starter", "professional", "enterprise"].includes(data.subscription_tier);
  const days = daysRemaining(data.trial_ends_at);

  const metrics = Object.entries(data.usage) as [string, UsageEntry][];
  const maxPct = Math.max(...metrics.map(([, entry]) => usagePercent(entry)));
  const nearingLimit = maxPct >= 80;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <CardTitle>Usage & Limits</CardTitle>
              <Badge variant={isTrial ? "outline" : "default"}>
                {tierLabel(data.subscription_tier)}
              </Badge>
            </div>
            <CardDescription>
              {isTrial && days !== null
                ? `Trial expires in ${days} day${days !== 1 ? "s" : ""}. Upgrade for unlimited access.`
                : isLegacy
                  ? "Legacy Professional plan with unlimited access."
                  : isPaid
                    ? "Current month usage for your plan."
                    : "Current month usage for your plan."}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => void fetchUsage(true)}
            disabled={refreshing}
            aria-label="Refresh usage data"
          >
            <RefreshCw className={cn("size-4", refreshing && "animate-spin")} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Progress bars for each metric */}
        <div className="grid gap-4 sm:grid-cols-1">
          {metrics.map(([key, entry]) => (
            <UsageBar
              key={key}
              label={METRIC_LABELS[key] ?? key}
              entry={entry}
            />
          ))}
        </div>

        {/* Upgrade CTA for trial users nearing limits */}
        {isTrial && nearingLimit ? (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950/40">
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                You&apos;re approaching your plan limits.
              </p>
              <Button
                size="sm"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("shippeek:open-upgrade-modal"));
                }}
              >
                Upgrade now
                <ArrowRight className="size-3.5" />
              </Button>
            </div>
          </div>
        ) : null}

        {/* Upgrade link for trial users not yet nearing limits */}
        {isTrial && !nearingLimit ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              window.dispatchEvent(new CustomEvent("shippeek:open-upgrade-modal"));
            }}
          >
            View upgrade options
            <ArrowRight className="size-3.5" />
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
